import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import PageHeader from "@/components/base/PageHeader";
import type { Work } from "@/types";
import { profile } from "@/mocks/profile";
import { works as rawWorks } from "@/mocks/works";

export default function Profile() {
  useDocumentMeta({
    title: "舒淇个人资料 - 生日、籍贯与影视作品年表",
    description:
      "舒淇个人资料页，详细介绍舒淇的出生日期、籍贯、职业等基本信息，梳理其演艺经历与主要影视作品年表，全面了解这位华语影坛实力女演员。",
    keywords: "舒淇个人资料,舒淇简介,舒淇生日,舒淇年表",
  });

  const works = (rawWorks as unknown as Work[])
    .slice()
    .sort((a, b) => b.year - a.year);

  return (
    <div>
      <PageHeader
        eyebrow="Profile"
        title="舒淇个人资料"
        description="从模特到华语影坛实力女演员，了解舒淇的演艺历程、重要荣誉与主要影视作品年表。"
      />

      <section className="max-w-content mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-28">
              <div className="w-full max-w-sm mx-auto lg:mx-0 aspect-[4/5] rounded-lg overflow-hidden bg-background-200 border border-background-200">
                <img
                  src={profile.avatar}
                  alt="舒淇 个人资料写真照"
                  title="舒淇 个人资料"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="mt-6 max-w-sm mx-auto lg:mx-0">
                <h2 className="font-heading text-2xl font-bold text-foreground-950">
                  {profile.name}
                </h2>
                <p className="text-xs tracking-[0.3em] uppercase text-secondary-500 mt-1">
                  {profile.enName}
                </p>
                <dl className="mt-6 space-y-4">
                  {profile.facts.map((fact) => (
                    <div
                      key={fact.label}
                      className="flex items-start justify-between gap-4 border-b border-background-200 pb-3"
                    >
                      <dt className="text-sm text-secondary-500">{fact.label}</dt>
                      <dd className="text-sm font-medium text-foreground-950 text-right">
                        {fact.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-12">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                人物简介
              </h2>
              <span className="block h-px w-12 bg-accent-400 mt-4 mb-5" />
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                {profile.intro}
              </p>
              <div className="mt-5 space-y-4">
                {profile.bio.map((paragraph) => (
                  <p
                    key={paragraph.slice(0, 12)}
                    className="text-sm md:text-base text-foreground-600 leading-loose"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                主要荣誉
              </h2>
              <span className="block h-px w-12 bg-accent-400 mt-4 mb-5" />
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {profile.awards.map((award) => (
                  <li
                    key={award}
                    className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3.5"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 shrink-0">
                      <i className="ri-award-line" />
                    </span>
                    <span className="text-sm text-foreground-800">{award}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                影视作品年表
              </h2>
              <span className="block h-px w-12 bg-accent-400 mt-4 mb-6" />
              <ol className="relative border-l border-background-300 ml-2">
                {works.map((work) => (
                  <li key={work.id} className="ml-6 pb-6 last:pb-0">
                    <span className="absolute -left-[7px] w-3.5 h-3.5 rounded-full bg-accent-500 border-2 border-background-50" />
                    <Link
                      to={`/works/${work.id}`}
                      className="group block rounded-lg border border-background-200 bg-background-50 p-4 hover:border-accent-300 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <span className="font-heading text-base font-semibold text-foreground-950 group-hover:text-accent-600 transition-colors">
                          {work.title}
                        </span>
                        <span className="text-xs text-secondary-500">
                          {work.year} · {work.type}
                        </span>
                      </div>
                      <p className="mt-1.5 text-xs text-foreground-600">
                        导演 {work.director} · 饰演 {work.role}
                      </p>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}